import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import * as am4themes_animated from "@amcharts/amcharts4/themes/animated";
import { FiUpload } from "react-icons/fi";
import axios from "axios"; // Import Axios
import { useSelector } from "react-redux";
import ErrorModal from "../Security/ErrorModal"; // Import the modal
import { Endpoints } from "../../Global/Endpoints";
import { useTranslation } from 'react-i18next';

// Enable animated theme
am4core.useTheme(am4themes_animated.default);

const PieChartComponent = forwardRef(({ used, remaining, total, accumulatorType, category }, ref) => {
  const { t } = useTranslation();
  const chartRef = useRef(null);
  const email = useSelector((state) => state.email.email); // Get email from Redux
  const token = localStorage.getItem("AccessToken");
  const [showModal, setShowModal] = useState(false); // State to control modal visibility
  const [modalMessage, setModalMessage] = useState(""); // State for modal message
  const [loading, setLoading] = useState(false); // State to control loading
  const [issharereport, setIssharereport] = useState(false); // State to control if it's share report action
  const ssoData = useSelector((state) => state.sso.ssoUserData);
  const isHighmarkPortal = ssoData?.Portal === "Highmark";
  const unlimited = total >= 99 && category === "Cycle Limit";

  useEffect(() => {
    if (unlimited || !chartRef.current) return;
    const chart = am4core.create(chartRef.current, am4charts.PieChart);
    chart.logo.disabled = true;

    // Create slices for used
    const usedSlices = Array.from({ length: used }, () => ({
      value: 1,
      color: isHighmarkPortal ? am4core.color("#0074c9") : am4core.color("#EC008C"),
    }));

    // Create slices for remaining
    const remainingSlices = Array.from({ length: remaining }, () => ({
      value: 1,
      color: isHighmarkPortal ? am4core.color("#d9d9d9") : am4core.color("#d9d9d9"),
    }));

    // Combine all slices
    chart.data = [...usedSlices, ...remainingSlices];

    const pieSeries = chart.series.push(new am4charts.PieSeries());
    pieSeries.dataFields.value = "value";
    pieSeries.dataFields.category = "category";

    // Disable tooltips
    pieSeries.slices.template.tooltipText = "";

    // Remove labels
    pieSeries.labels.template.disabled = true;

    // Slice styling
    pieSeries.slices.template.stroke = am4core.color("#fff");
    pieSeries.slices.template.strokeWidth = 2;
    pieSeries.slices.template.strokeOpacity = 1;
    pieSeries.slices.template.events.on("hit", (event) => {
      const slice = event.target;

      // Ensure the clicked slice is fully initialized
      if (!slice.appeared) {
        return; // Skip handling if not interactive
      }

      const active = slice.isActive;

      // Clear active states for all slices
      pieSeries.slices.each((item) => {
        if (item !== slice) {
          item.isActive = false;
        }
      });

      // Toggle the active state for the clicked slice
      slice.isActive = !active;
    });

    // Add slice colors dynamically
    pieSeries.slices.template.propertyFields.fill = "color";

    // Full circle layout
    chart.innerRadius = 0;

    // Remove legend if not required
    const legend = chart.createChild(am4charts.Legend);
    legend.disabled = true;

    // Animate on load
    pieSeries.hiddenState.properties.opacity = 0;
    pieSeries.hiddenState.properties.endAngle = -90;
    pieSeries.hiddenState.properties.startAngle = -90;

    // Cleanup
    return () => chart.dispose();
  }, [used, remaining, isHighmarkPortal, unlimited]);


  // Function to handle Share Report click
  const handleShareReport = async () => {
    setLoading(true); // Start loading
    const params = { EmailId: email, DeviceType: "PatientPortal" };

    try {
      const response = await axios.post(
        Endpoints.SendAccumulatorDetails,
        `_Params=${encodeURIComponent(JSON.stringify(params))}`,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setLoading(false); // Stop loading

      if (response.data.Message === "Successful" && response.data.Result === 1) {
        setModalMessage(t('successfullyexported'));
        setIssharereport(true); // Indicate that this is a share report action
        setShowModal(true); // Show the modal
      } else {
        setModalMessage(t('failedtosharethereport'));
        setIssharereport(false); // Reset the flag for non-share report action
        setShowModal(true); // Show the modal
      }
    } catch (error) {
      setLoading(false); // Stop loading on error
      setModalMessage(t('erroroccured'));
      setIssharereport(false); // Reset the flag for non-share report action
      setShowModal(true); // Show the modal
    }
  };

  useImperativeHandle(ref, () => ({
    handleShareReport,
  }));

  const handleOkClick = () => {
    setShowModal(false); // Close the modal when the OK button is clicked
  };

  const handleCancelClick = () => {
    setShowModal(false); // Close the modal when the Cancel button is clicked
  };

  return (
    <>
      {/* Conditionally Render Pie Chart or Message */}
      {(used === 0 && remaining === 0 )? (
        <div className="no-data-message">
        </div>
      ) : (
        <>
          {/* Pie Chart */}
          {!unlimited && (
            <div ref={chartRef} className="mt-4 order-1 pie-chart-wrapper"></div>
          )}

          <div className={unlimited ? "mb-4 unlimited-accumulator-content" :"col-md-6 mt-4 order-1"}>
            {/* Left-Aligned Accumulator Type */}
            {accumulatorType !== "" && (
              <div className="my-3 my-sm-3 my-lg-5">
                <span>{accumulatorType}</span>
              </div>
            )}

            {/* Share Report Button */}
           
          </div>
        </>
      )}

      {/* Error Modal - Default with OK and Cancel */}
      {!issharereport && (
        <ErrorModal
          show={showModal}
          message={modalMessage}
          onClose={() => setShowModal(false)}
          onOkay={handleOkClick}
          onCancel={handleCancelClick} // Pass cancel action handler
        />
      )}
      {issharereport && (
        <ErrorModal
          show={showModal}
          message={modalMessage}
          onClose={() => setShowModal(false)}
        />
      )}

      {/* Show Loading Spinner */}
      {loading && (
        <div className="loading-container">
          {/* Replace this with your existing spinner */}
          <div className="loading"></div>
          <h2 className="loading-text">{t("loading")}</h2>
        </div>
      )}
    </>
  );
});

export default PieChartComponent;
